import React, { useRef, useEffect, useState } from "react";
import styled, { ThemeProvider, css } from "styled-components";
import { DarkTheme } from "./Themes";
import LogoComponent from "../subComponents/LogoComponent";
import SocialIcons from "../subComponents/SocialIcons";
import PowerButton from "../subComponents/PowerButton";
import { Work } from "../data/WorkData";
import Card from "../subComponents/Card";
import { YinYang } from "./AllSvgs";
import BigTitle from "../subComponents/BigTitle";
import { motion, useAnimation, useMotionValue, useTransform } from "framer-motion";

const Box = styled.div`
  background-color: ${(props) => props.theme.body};
  height: 100vh;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 100%;
`;

const MainContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  position: relative;
  overflow: hidden;
  background-color: ${(props) => props.theme.body};
  
  /* Ensure space for social icons on the left */
  padding-left: 4rem;
  
  @media (max-width: 768px) {
    padding-left: 3rem;
    width: 100%;
  }
  
  @media (max-width: 480px) {
    padding-left: 2.5rem;
    width: 100%;
  }
`;

const CardsContainer = styled.div`
  width: 80%;
  max-width: 1200px;
  overflow-x: auto;
  padding: 1rem 0;
  -ms-overflow-style: none;
  scrollbar-width: none;
  scroll-behavior: smooth;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform, scroll-position;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x;
  position: relative;
  background-color: ${(props) => props.theme.body};
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  overflow-y: hidden;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  
  @media (max-width: 768px) {
    width: 75%;
    max-width: none;
    /* Ensure cards fit perfectly for one-at-a-time display */
    display: flex;
    align-items: center;
  }
  
  @media (max-width: 480px) {
    width: 70%;
    max-width: none;
  }
`;

const CardsRow = styled(motion.div)`
  display: flex;
  gap: 2rem;
  width: max-content;
  will-change: transform;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  padding-right: 2rem;
  background-color: ${(props) => props.theme.body};
  min-width: 100%;
  
  /* Add padding to prevent black space at the end */
  &::after {
    content: "";
    min-width: 2rem;
    height: 1px;
    display: block;
  }
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const CardWrapper = styled.div`
  background-color: ${(props) => props.theme.body};
  min-width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const YinYangContainer = styled.div`
  position: fixed;
  right: 5vw;
  bottom: 5vh;
  width: 80px;
  height: 80px;
  z-index: 10;
  transition: transform 1.5s cubic-bezier(0.68, -0.55, 0.27, 1.55);
  transform: ${props => `rotate(${props.rotation}deg)`};
  
  @media (max-width: 768px) {
    right: 10px;
    bottom: 10px;
  }
`;

// Navigation buttons
const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: ${props => props.theme.text};
  color: ${props => props.theme.body};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: none;
  font-size: 1.5rem;
  z-index: 10;
  opacity: 0.8;
  transition: opacity 0.3s ease;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  
  &:hover {
    opacity: 1;
  }
  
  &.prev {
    left: 7rem; /* Move closer to cards */
    
    @media (max-width: 768px) {
      left: 5rem;
      width: 32px;
      height: 32px;
      font-size: 1.2rem;
    }
    
    @media (max-width: 480px) {
      left: 3.5rem;
      width: 26px;
      height: 26px;
      font-size: 1rem;
    }
  }
  
  &.next {
    right: 4rem;
    
    @media (max-width: 768px) {
      right: 2.5rem;
      width: 32px;
      height: 32px;
      font-size: 1.2rem;
    }
    
    @media (max-width: 480px) {
      right: 2rem;
      width: 26px;
      height: 26px;
      font-size: 1rem;
    }
  }
`;

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      duration: 0.5,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeInOut"
    }
  }
};

const ProjectPage = () => {
  const containerRef = useRef(null);
  const [rotation, setRotation] = useState(0);
  const controls = useAnimation();
  const x = useMotionValue(0);
  const velocity = useMotionValue(0);
  const lastX = useRef(0);
  const lastTime = useRef(0);
  const isDragging = useRef(false);
  
  // Add swipe functionality with advanced animations
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    let isDown = false;
    let startX;
    let scrollLeft;
    let hasMoved = false;
    let lastVelocity = 0;
    let animationFrame;
    
    // Calculate the drag boundaries based on actual content
    const updateDragConstraints = () => {
      if (!containerRef.current) return { left: 0, right: 0 };
      const containerWidth = containerRef.current.clientWidth;
      const contentWidth = containerRef.current.scrollWidth;
      return { left: -(contentWidth - containerWidth), right: 0 };
    };
    
    // Fix scroll position to prevent black spaces
    const fixScrollPosition = () => {
      if (!containerRef.current) return;
      
      const container = containerRef.current;
      const maxScroll = container.scrollWidth - container.clientWidth;
      
      // If scrolled past the end, scroll back to the max
      if (container.scrollLeft > maxScroll) {
        container.scrollLeft = maxScroll;
      }
      
      // If scrolled before the start, scroll back to 0
      if (container.scrollLeft < 0) {
        container.scrollLeft = 0;
      }
    };
    
    const calculateVelocity = (currentX, currentTime) => {
      const deltaX = currentX - lastX.current;
      const deltaTime = currentTime - lastTime.current;
      return deltaTime > 0 ? deltaX / deltaTime : 0;
    };
    
    const applyMomentum = (velocity) => {
      const isMobile = window.innerWidth <= 768;
      const currentScroll = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;
      
      if (isMobile) {
        // Mobile: Move one card at a time based on swipe direction
        const cardElement = container.querySelector('[data-card]');
        const cardWidth = cardElement ? cardElement.offsetWidth : 220;
        const gap = window.innerWidth <= 480 ? 16 : 24; // 1rem = 16px, 1.5rem = 24px
        const cardWithGap = cardWidth + gap;
        const threshold = 50; // Minimum swipe velocity to trigger navigation
        
        if (Math.abs(velocity) > threshold) {
          const direction = velocity > 0 ? -1 : 1; // Positive velocity = swipe right = go left
          const targetScroll = Math.max(0, Math.min(maxScroll, currentScroll + (direction * cardWithGap)));
          
          container.scrollTo({
            left: targetScroll,
            behavior: 'smooth'
          });
        } else {
          // Snap to nearest card position if velocity is too low
          const nearestCardPosition = Math.round(currentScroll / cardWithGap) * cardWithGap;
          const boundedPosition = Math.max(0, Math.min(maxScroll, nearestCardPosition));
          
          container.scrollTo({
            left: boundedPosition,
            behavior: 'smooth'
          });
        }
      } else {
        // Desktop/Tablet: Keep existing behavior
        const cardWidth = 400; // Approximate card width
        const momentum = velocity * 800; // Reduced from 1000 for more stable behavior
        const targetScroll = container.scrollLeft + momentum;
        
        // Use a more precise calculation for snap position
        const itemWidth = cardWidth + 32; // Card width + gap
        const snapPosition = Math.round(targetScroll / itemWidth) * itemWidth;
        
        // Constrain to valid scroll bounds
        const boundedPosition = Math.max(0, Math.min(maxScroll, snapPosition));
        
        container.scrollTo({
          left: boundedPosition,
          behavior: 'smooth'
        });
      }
      
      // Check and fix position after animation completes
      setTimeout(fixScrollPosition, 500);
    };
    
    const handleStart = (clientX) => {
      isDown = true;
      hasMoved = false;
      startX = clientX - container.offsetLeft;
      scrollLeft = container.scrollLeft;
      lastX.current = clientX;
      lastTime.current = performance.now();
      container.style.scrollBehavior = 'auto';
      container.style.touchAction = 'none';
    };
    
    const handleMove = (clientX, currentTime) => {
      if (!isDown) return;
      
      const x = clientX - container.offsetLeft;
      const walk = (x - startX) * 1.2;
      
      if (Math.abs(walk) > 20) {
        hasMoved = true;
      }
      
      // Calculate velocity
      const currentVelocity = calculateVelocity(clientX, currentTime);
      lastVelocity = currentVelocity;
      
      // Apply easing based on velocity
      const easedWalk = walk * (1 - Math.abs(currentVelocity) / 2000);
      
      requestAnimationFrame(() => {
        container.scrollLeft = scrollLeft - easedWalk;
      });
      
      lastX.current = clientX;
      lastTime.current = currentTime;
    };
    
    const handleEnd = () => {
      if (isDown && hasMoved) {
        // Apply momentum based on last velocity
        applyMomentum(lastVelocity);
        setRotation(prev => prev + 360);
        
        // Add a final check to ensure we're within bounds
        setTimeout(fixScrollPosition, 500);
      }
      
      isDown = false;
      container.style.scrollBehavior = 'smooth';
      container.style.touchAction = 'pan-x';
    };
    
    const handleMouseDown = (e) => {
      e.preventDefault();
      handleStart(e.pageX);
    };
    
    const handleMouseMove = (e) => {
      handleMove(e.pageX, performance.now());
    };
    
    const handleMouseUp = () => {
      handleEnd();
    };
    
    const handleTouchStart = (e) => {
      handleStart(e.touches[0].pageX);
    };
    
    const handleTouchMove = (e) => {
      handleMove(e.touches[0].pageX, performance.now());
    };
    
    const handleTouchEnd = () => {
      handleEnd();
    };
    
    // Add event listeners
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseUp);
    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchmove', handleTouchMove);
    container.addEventListener('touchend', handleTouchEnd);
    
    // Additional check on window resize to fix scroll position
    const handleResize = () => {
      fixScrollPosition();
    };
    
    window.addEventListener('resize', handleResize);
    
    // Add event to fix scroll position after everything has loaded
    window.addEventListener('load', fixScrollPosition);
    
    // Cleanup
    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseUp);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('load', fixScrollPosition);
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, []);
  
  // Enhanced scroll functions with better boundary handling
  const scrollLeft = () => {
    if (containerRef.current) {
      const currentScroll = containerRef.current.scrollLeft;
      const isMobile = window.innerWidth <= 768;
      
      if (isMobile) {
        // Mobile: Move one card at a time using actual card dimensions
        const cardElement = containerRef.current.querySelector('[data-card]');
        const cardWidth = cardElement ? cardElement.offsetWidth : 220;
        const gap = window.innerWidth <= 480 ? 16 : 24; // 1rem = 16px, 1.5rem = 24px
        const cardWithGap = cardWidth + gap;
        
        // If we're at the beginning, do a small bounce effect
        if (currentScroll <= 10) {
          containerRef.current.scrollTo({
            left: 5,
            behavior: 'smooth'
          });
          setTimeout(() => {
            containerRef.current.scrollTo({
              left: 0,
              behavior: 'smooth'
            });
          }, 150);
          setRotation(prev => prev + 180);
          return;
        }
        
        const targetScroll = Math.max(0, currentScroll - cardWithGap);
        containerRef.current.scrollTo({
          left: targetScroll,
          behavior: 'smooth'
        });
      } else {
        // Desktop/Tablet: Keep existing behavior
        const cardWidth = 400;
        
        // If we're at the beginning, do a small bounce effect to indicate we're at the start
        if (currentScroll <= 10) {
          containerRef.current.scrollTo({
            left: 5,
            behavior: 'smooth'
          });
          setTimeout(() => {
            containerRef.current.scrollTo({
              left: 0,
              behavior: 'smooth'
            });
          }, 150);
          setRotation(prev => prev + 180); // Half rotation for visual feedback
          return;
        }
        
        const targetScroll = Math.max(0, currentScroll - cardWidth);
        
        containerRef.current.scrollTo({
          left: targetScroll,
          behavior: 'smooth'
        });
      }
      setRotation(prev => prev + 360);
    }
  };
  
  const scrollRight = () => {
    if (containerRef.current) {
      const currentScroll = containerRef.current.scrollLeft;
      const isMobile = window.innerWidth <= 768;
      const maxScroll = containerRef.current.scrollWidth - containerRef.current.clientWidth - 10;
      
      if (isMobile) {
        // Mobile: Move one card at a time using actual card dimensions
        const cardElement = containerRef.current.querySelector('[data-card]');
        const cardWidth = cardElement ? cardElement.offsetWidth : 220;
        const gap = window.innerWidth <= 480 ? 16 : 24; // 1rem = 16px, 1.5rem = 24px
        const cardWithGap = cardWidth + gap;
        const targetScroll = Math.min(maxScroll, currentScroll + cardWithGap);
        
        containerRef.current.scrollTo({
          left: targetScroll,
          behavior: 'smooth'
        });
      } else {
        // Desktop/Tablet: Keep existing behavior
        const cardWidth = 400;
        const targetScroll = Math.min(maxScroll, currentScroll + cardWidth);
        
        containerRef.current.scrollTo({
          left: targetScroll,
          behavior: 'smooth'
        });
      }
      setRotation(prev => prev + 360);
    }
  };

  // Trigger rotation on scroll events
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    let lastScrollLeft = container.scrollLeft;
    let isThrottled = false;
    
    const handleScroll = () => {
      if (isThrottled) return;
      
      // Calculate scroll difference
      const currentScrollLeft = container.scrollLeft;
      const scrollDiff = Math.abs(currentScrollLeft - lastScrollLeft);
      
      // Only rotate if there was a significant scroll
      if (scrollDiff > 20) {
        setRotation(prev => prev + 360);
        lastScrollLeft = currentScrollLeft;
        
        // Throttle to prevent excessive rotation
        isThrottled = true;
        setTimeout(() => {
          isThrottled = false;
        }, 300);
      }
    };
    
    container.addEventListener('scroll', handleScroll);
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <ThemeProvider theme={DarkTheme}>
      <Box>
        <LogoComponent theme="dark" />
        <SocialIcons theme="dark" />
        <PowerButton />

        <MainContainer>
          <NavButton className="prev" onClick={scrollLeft}>
            &#8592;
          </NavButton>
          
          <CardsContainer 
            ref={containerRef}
            onScroll={(e) => {
              // Check if we've scrolled to the end and fix if needed
              const container = e.currentTarget;
              const maxScroll = container.scrollWidth - container.clientWidth;
              if (Math.abs(container.scrollLeft - maxScroll) < 5) {
                // We're at the end, ensure we don't see black space
                container.scrollLeft = maxScroll;
              }
            }}
          >
            <CardWrapper>
              <CardsRow
                variants={container}
                initial="hidden"
                animate="show"
                drag="x"
                dragConstraints={{ left: -2000, right: 0 }} // Wider constraint with dynamic update
                dragElastic={0.05} // Reduced elasticity for more stable dragging
                onDragEnd={(e, info) => {
                  const velocity = info.velocity.x;
                  const isMobile = window.innerWidth <= 768;
                  const currentScroll = containerRef.current.scrollLeft;
                  const maxScroll = containerRef.current.scrollWidth - containerRef.current.clientWidth;
                  
                  if (Math.abs(velocity) > 200) { // Reduced threshold for more responsive swiping
                    const direction = velocity > 0 ? -1 : 1;
                    
                    if (isMobile) {
                      // Mobile: Move one card at a time using actual card dimensions
                      const cardElement = containerRef.current.querySelector('[data-card]');
                      const cardWidth = cardElement ? cardElement.offsetWidth : 220;
                      const gap = window.innerWidth <= 480 ? 16 : 24; // 1rem = 16px, 1.5rem = 24px
                      const cardWithGap = cardWidth + gap;
                      const targetScroll = Math.max(0, Math.min(maxScroll, currentScroll + (direction * cardWithGap)));
                      
                      containerRef.current.scrollTo({
                        left: targetScroll,
                        behavior: 'smooth'
                      });
                    } else {
                      // Desktop/Tablet: Keep existing behavior
                      const cardWidth = 400;
                      const targetScroll = Math.max(0, Math.min(maxScroll, currentScroll + (direction * cardWidth)));
                      
                      containerRef.current.scrollTo({
                        left: targetScroll,
                        behavior: 'smooth'
                      });
                    }
                  }
                }}
              >
                {Work.map((work) => (
                  <Card key={work.id} data={work} variants={item} />
                ))}
              </CardsRow>
            </CardWrapper>
          </CardsContainer>
          
          <NavButton className="next" onClick={scrollRight}>
            &#8594;
          </NavButton>
        </MainContainer>

        <YinYangContainer rotation={rotation}>
          <YinYang width={80} height={80} fill={DarkTheme.text} />
        </YinYangContainer>

        <BigTitle text="PROJECTS" top="15%" right="20%" />
        <BigTitle text="SWIPE" top="90%" right="30%" />
      </Box>
    </ThemeProvider>
  );
};

export default ProjectPage;